// import: styles
import { Backdrop, Box, Popover } from '@mui/material';
// import: assets
import { ReactComponent as SettingsMenu } from 'core/assets/svg/app-bar/menu.svg';
import { ReactComponent as Download } from 'core/assets/svg/export-image/download.svg';
import { ReactComponent as Seed } from 'core/assets/svg/export-image/seed.svg';
import { ReactComponent as ShareLink } from 'core/assets/svg/export-image/share-link.svg';
import { ReactComponent as Twitter } from 'core/assets/svg/export-image/twitter.svg';
import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import { ReactComponent as NarratorIcon } from 'core/assets/svg/modal-icons/narrator-icon.svg';
import { ReactComponent as Upscale8kIcon } from 'core/assets/svg/studio-tab/8k-upscale.svg';
import { ReactComponent as CheckIcon } from 'core/assets/svg/studio-tab/feed-item/check-icon.svg';
import { ReactComponent as Delete } from 'core/assets/svg/studio-tab/trash.svg';
import { ReactComponent as TrueTouch } from 'core/assets/svg/studio-tab/true-touch.svg';
import { ReactComponent as VideoAnyoneIcon } from 'core/assets/svg/studio-tab/video-frame-icon.svg';

import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';

// import: services
// import: components
import BaseTypography from 'core/components/typography/BaseTypography';
import { MediaType } from 'core/constants/enums/MediaType.ts';
import { MediaTypeView } from 'core/constants/enums/MediaTypeView.ts';
import { StudioSidebarViews } from 'core/constants/enums/StudioSidebarViews.ts';
// import: constants
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import {
	disabledStyles,
	expandedStyles,
	expandOnHoverStyles,
	exportImageButtonsStyles,
} from 'core/layouts/components/export-image-buttons/ExportImageButtonsStyles';
import { itemChipStyles } from 'core/layouts/components/settings-sidebar/selected-items-chips-component/components/item-chip/ItemChipStyles';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { TEXT_TO_IMAGE } from 'core/navigation/Routes';
import {
	uploadLipSyncVideoAsync,
	uploadVideoAnyoneImageLinkAsync,
} from 'core/store/middleware/StudioCanvasMiddleware';

// import: redux
import {
	setStudioAppBarMediaType,
	setStudioSidebarView,
	setStudioUpscaleImage,
	studioSelectedImagesSelector,
	toggleMultiDeletePromptConfirmationModal,
	updateStudioSelectedImages,
	uploadStudioTrueTouchImageLinkAsync,
} from 'core/store/reducers/studioReducer/StudioSlice';

// import: utils
import { copyToClipboard, downloadMedia, validateImageDimension } from 'core/utils/utils';
import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TwitterShareButton } from 'react-share';

// import: lazy load components

function ExportImageButtonComponent({
	id,
	icon,
	expandComp = null,
	keepExpanded = false,
	onClickFunc,
	disabled = false,
	upSize = false,
	children,
}) {
	return (
		<BaseButton
			id={id}
			styles={[
				exportImageButtonsStyles.exportImgButton,
				!upSize && exportImageButtonsStyles.regularExportImgButton,
				upSize && exportImageButtonsStyles.upsizedExportImgButton,
				expandComp && expandOnHoverStyles,
				expandComp && keepExpanded && expandedStyles,
			]}
			disabled={disabled}
			onClick={onClickFunc}
		>
			{icon}
			{expandComp && expandComp}
			{children}
		</BaseButton>
	);
}

export function IconButton({ id, icon, onClick, disabled = false, upSize = false }) {
	return (
		<ExportImageButtonComponent
			id={id}
			upSize={upSize}
			onClickFunc={onClick}
			icon={icon}
			disabled={disabled}
		/>
	);
}

export function ShareLinkButton({ id, outputImage, disabled = false, upSize = false, mediaType }) {
	const onClickHandler = (e) => {
		e.preventDefault();
		e.stopPropagation();
		copyToClipboard(outputImage);
		if (mediaType === MediaType.VIDEO) {
			sendToast(ToastType.VIDEO_LINK_COPIED);
		} else sendToast(ToastType.LINK_COPIED);
	};

	return (
		<ExportImageButtonComponent
			id={id}
			upSize={upSize}
			icon={<ShareLink />}
			onClickFunc={onClickHandler}
			disabled={disabled}
		/>
	);
}

export function ImageDetail({ id, icon, disabled = false, upSize = false, label }) {
	return (
		<ExportImageButtonComponent
			id={id}
			onClickFunc={(e) => e.stopPropagation()}
			upSize={upSize}
			icon={
				<Box id="selected-image-container" sx={itemChipStyles.selectedModelImageContainer}>
					<BaseImage src={icon} />
				</Box>
			}
			expandComp={
				<BaseTypography variant="b2_caption" styles={{ color: 'colors.light3', marginLeft: '6px' }}>
					{label}
				</BaseTypography>
			}
			keepExpanded
			disabled={disabled}
		/>
	);
}

export const DownloadImageButton = React.memo(
	function DownloadImageButtonC({ id, outputImage, disabled = false, upSize = false }) {
		const onClickHandler = (e) => {
			e.preventDefault();
			e.stopPropagation();
			downloadMedia(outputImage);
		};
		return (
			<ExportImageButtonComponent
				id={id}
				upSize={upSize}
				icon={<Download />}
				onClickFunc={onClickHandler}
				disabled={disabled}
			/>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.outputImage === nextProps.outputImage &&
			prevProps.disabled === nextProps.disabled &&
			prevProps.upSize === nextProps.upSize
		);
	},
);

export function TwitterImageShareButton({ outputImage, disabled = false, upSize = false }) {
	return (
		<TwitterShareButton
			url={outputImage || 'https://some_url_to_pass_assert.com'}
			title={`Checkout this AI image created on @rendernet_ai 🚀\n
#rendernet #ai #aiart #aiimage #stablediffusion\n`}
			disabled={disabled}
			resetButtonStyle
			disabledStyle={{ cursor: 'initial' }}
			upSize={upSize}
		>
			<Box
				sx={[
					exportImageButtonsStyles.exportImgButton,
					!upSize && exportImageButtonsStyles.regularExportImgButton,
					upSize && exportImageButtonsStyles.upsizedExportImgButton,
					disabled ? disabledStyles : {},
				]}
			>
				<Twitter />
			</Box>
		</TwitterShareButton>
	);
}

export const DeleteImageButton = React.memo(
	function DeleteImageButtonC({
		id,
		imageGenerationId,
		imageBatchId,
		disabled = false,
		upSize = false,
		title = null,
		subtitle = null,
	}) {
		const dispatch = useDispatch();
		const onClickHandler = (e) => {
			e.preventDefault();
			e.stopPropagation();
			dispatch(
				toggleMultiDeletePromptConfirmationModal({
					open: true,
					title,
					subtitle,
					prompt: [
						{
							generationId: imageGenerationId,
							batchId: imageBatchId,
						},
					],
				}),
			);
		};

		return (
			<ExportImageButtonComponent
				id={id}
				icon={<Delete />}
				onClickFunc={onClickHandler}
				disabled={disabled}
				upSize={upSize}
			/>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.disabled === nextProps.disabled &&
			prevProps.imageGenerationId === nextProps.imageGenerationId &&
			prevProps.imageBatchId === nextProps.imageBatchId &&
			prevProps.upSize === nextProps.upSize &&
			prevProps.title === nextProps.title &&
			prevProps.subtitle === nextProps.subtitle
		);
	},
);

/**
 * @description: Function to handle the media selection/deselection
 * @param {Event} e
 * @param {Dispatch<AnyAction>} dispatch
 * @param {{generationId: string, batchId: string, media: string}} mediaData
 * */
export const onMediaSelect = (e, dispatch, mediaData) => {
	e.preventDefault();
	e.stopPropagation();
	dispatch(updateStudioSelectedImages(mediaData));
};

export const SelectMediaButton = React.memo(
	function SelectMediaButtonC({ generationId, batchId, media }) {
		const dispatch = useDispatch();
		const selectedImages = useSelector(studioSelectedImagesSelector);

		return (
			<BaseButton
				id="select-button"
				disableRipple
				onClick={(e) => onMediaSelect(e, dispatch, { generationId, batchId, media })}
				styles={{
					minWidth: 'min-content',
					padding: '0px',
					alignSelf: 'flex-start',
				}}
			>
				<Box sx={exportImageButtonsStyles.selectMedia}>
					{selectedImages.some((image) => image.generationId === generationId) ? (
						<CheckIcon />
					) : null}
				</Box>
			</BaseButton>
		);
	},
	(prevProps, nextProps) =>
		prevProps.generationId === nextProps.generationId &&
		prevProps.batchId === nextProps.batchId &&
		prevProps.media === nextProps.media,
);

export const VideoAnyoneImageButton = React.memo(
	function VideoAnyoneImageButtonC({
		id,
		imageUrl,
		imageId,
		width,
		height,
		aspectRatio,
		disabled = false,
		onClick = () => {},
		upSize = false,
		isExpanded = true,
		children,
	}) {
		const dispatch = useDispatch();
		const onClickHandler = (e) => {
			e.stopPropagation();
			const isValid = validateImageDimension(height, width, aspectRatio);
			if (isValid) {
				dispatch(uploadVideoAnyoneImageLinkAsync(imageUrl, imageId, TEXT_TO_IMAGE));
			}
			dispatch(setStudioAppBarMediaType('Video'));
			onClick();
		};
		return (
			<ExportImageButtonComponent
				id={id}
				icon={<VideoAnyoneIcon />}
				onClickFunc={onClickHandler}
				disabled={disabled}
				keepExpanded={isExpanded}
				upSize={upSize}
			>
				{children}
			</ExportImageButtonComponent>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.imageUrl === nextProps.imageUrl &&
			prevProps.imageId === nextProps.imageId &&
			prevProps.disabled === nextProps.disabled &&
			prevProps.upSize === nextProps.upSize &&
			prevProps.isExpanded === nextProps.isExpanded
		);
	},
);

export const NarratorVideoButton = React.memo(
	function NarratorVideoButtonC({
		id,
		imageUrl,
		imageId,
		disabled = false,
		onClick = () => {},
		upSize = false,
		isExpanded = true,
		children,
	}) {
		const dispatch = useDispatch();
		const onClickHandler = (e) => {
			e.stopPropagation();
			dispatch(setStudioAppBarMediaType(MediaTypeView.VIDEO));
			dispatch(setStudioSidebarView(StudioSidebarViews.NARRATOR));
			dispatch(uploadLipSyncVideoAsync({ id: imageId, url: imageUrl }, TEXT_TO_IMAGE));
			onClick();
		};
		return (
			<ExportImageButtonComponent
				id={id}
				icon={<NarratorIcon />}
				onClickFunc={onClickHandler}
				disabled={disabled}
				keepExpanded={isExpanded}
				upSize={upSize}
			>
				{children}
			</ExportImageButtonComponent>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.imageUrl === nextProps.imageUrl &&
			prevProps.imageId === nextProps.imageId &&
			prevProps.disabled === nextProps.disabled &&
			prevProps.upSize === nextProps.upSize &&
			prevProps.isExpanded === nextProps.isExpanded
		);
	},
);

export const UpscaleImageButton = React.memo(
	function UpscaleImageButtonC({
		id,
		outputImage,
		disabled = false,
		onClick = () => {},
		upSize = false,
		isExpanded = true,
	}) {
		const dispatch = useDispatch();
		const onClickHandler = useCallback(
			(e) => {
				e.stopPropagation();
				dispatch(setStudioUpscaleImage(outputImage));
				dispatch(setStudioSidebarView(StudioSidebarViews.UPSCALE_8K));
				onClick();
			},
			[dispatch, outputImage, onClick],
		);

		return (
			<ExportImageButtonComponent
				id={id}
				icon={<Upscale8kIcon />}
				onClickFunc={onClickHandler}
				disabled={disabled}
				keepExpanded={isExpanded}
				upSize={upSize}
			/>
		);
	},
	(prevProps, nextProps) =>
		prevProps.disabled === nextProps.disabled &&
		prevProps.onClick === nextProps.onClick &&
		prevProps.outputImage === nextProps.outputImage &&
		prevProps.upSize === nextProps.upSize &&
		prevProps.isExpanded === nextProps.isExpanded,
);

export const TrueTouchImageButton = React.memo(
	function TrueTouchImageButtonC({
		id,
		outputImage,
		disabled = false,
		onClick = () => {},
		upSize = false,
		isExpanded = true,
	}) {
		const dispatch = useDispatch();
		const onClickHandler = (e) => {
			e.stopPropagation();
			dispatch(uploadStudioTrueTouchImageLinkAsync(outputImage));
			dispatch(setStudioSidebarView(StudioSidebarViews.TRUE_TOUCH));
			onClick();
		};
		return (
			<ExportImageButtonComponent
				id={id}
				icon={<TrueTouch />}
				onClickFunc={onClickHandler}
				disabled={disabled}
				keepExpanded={isExpanded}
				upSize={upSize}
			/>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.outputImage === nextProps.outputImage &&
			prevProps.disabled === nextProps.disabled &&
			prevProps.upSize === nextProps.upSize &&
			prevProps.isExpanded === nextProps.isExpanded
		);
	},
);

export function MobileDrawerButton({
	id,
	children,
	isOpen,
	toggleDrawer,
	sx = {},
	upSize = false,
}) {
	const onClickFunc = (e) => {
		e.preventDefault();
		e.stopPropagation();
		toggleDrawer();
	};

	const anchor = useRef(null);
	return (
		<>
			<Backdrop open={isOpen} sx={{ zIndex: 1 }} onClick={onClickFunc} />
			<Box ref={anchor} sx={sx}>
				<ExportImageButtonComponent
					id={id}
					upSize={upSize}
					onClickFunc={onClickFunc}
					icon={<SettingsMenu />}
				/>
			</Box>
			<Popover
				onClose={onClickFunc}
				open={isOpen}
				anchorEl={anchor.current}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				sx={{
					'& .MuiPopover-paper': {
						backgroundColor: 'transparent',
					},
				}}
			>
				{children}
			</Popover>
		</>
	);
}

export function SeedButton({ id, seed, keepExpanded = false, disabled = false }) {
	const onClickHandler = (e) => {
		e.preventDefault();
		e.stopPropagation();
		copyToClipboard(seed);
		sendToast(ToastType.VALUE_COPIED);
	};

	return (
		<ExportImageButtonComponent
			id={id}
			icon={<Seed />}
			expandComp={
				<Box className="expanded-content" sx={[exportImageButtonsStyles.seedTextCont]}>
					<Box sx={[reusableStyles.flexRow, exportImageButtonsStyles.seedText]}>
						<BaseTypography variant="b1_regular" styles={{ flexShrink: 0, color: 'colors.light5' }}>
							Seed:{' '}
							<BaseTypography
								component="span"
								variant="b1_regular"
								styles={{ color: 'colors.light2' }}
							>
								{seed}
							</BaseTypography>
						</BaseTypography>

						<CopyDetail />
					</Box>
				</Box>
			}
			keepExpanded={keepExpanded}
			onClickFunc={onClickHandler}
			disabled={disabled}
		/>
	);
}
